import clsx from 'clsx'

type BadgeVariant = 'teal' | 'clay' | 'mint' | 'info' | 'warning' | 'success' | 'neutral'

interface BadgeProps {
  children: React.ReactNode
  variant?: BadgeVariant
  size?: 'sm' | 'md'
  className?: string
}

/**
 * Brand-styled badge component
 *
 * Per brand guidelines, uses warm colors and avoids aggressive red
 * for financial contexts
 */
export function Badge({
  children,
  variant = 'neutral',
  size = 'md',
  className,
}: BadgeProps) {
  const variants = {
    teal: 'bg-accent-soft text-accent',
    clay: 'bg-highlight-soft text-highlight',
    mint: 'bg-success-soft text-success',
    info: 'bg-surface-alt text-ink-muted',
    warning: 'bg-warning-soft text-warning',
    success: 'bg-success-soft text-success',
    neutral: 'bg-surface-alt text-ink-muted',
  }

  const sizes = {
    sm: 'px-2 py-0.5 text-[11px]',
    md: 'px-2.5 py-1 text-caption',
  }

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded-chip font-medium',
        variants[variant],
        sizes[size],
        className
      )}
    >
      {children}
    </span>
  )
}

type StatusType =
  | 'cleared'
  | 'in_progress'
  | 'action_needed'
  | 'new'
  | 'pending'
  | 'approved'
  | 'escalated'

interface StatusBadgeProps {
  status: StatusType
  className?: string
}

/**
 * Status badge with brand-appropriate terminology
 *
 * Key terminology mapping (per Brand Identity doc):
 * - "Overdue" -> "Action Needed" (warm orange, not red)
 * - "Debtors" -> "Families"
 * - "Outstanding Debts" -> "Current Balances"
 * - "Payment Demand" -> "Payment Reminder"
 */
export function StatusBadge({ status, className }: StatusBadgeProps) {
  const statusConfig: Record<StatusType, { label: string; variant: BadgeVariant }> = {
    cleared: { label: 'Cleared', variant: 'success' },
    in_progress: { label: 'In progress', variant: 'info' },
    action_needed: { label: 'Action Needed', variant: 'warning' },
    new: { label: 'New', variant: 'info' },
    pending: { label: 'Pending', variant: 'warning' },
    approved: { label: 'Approved', variant: 'success' },
    escalated: { label: 'Needs a person', variant: 'warning' },
  }

  const config = statusConfig[status] || statusConfig.pending

  return (
    <Badge variant={config.variant} className={className}>
      {config.label}
    </Badge>
  )
}
